"use strict"

import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Button } from "react-bootstrap"
import ResizeSensor from "css-element-queries/src/ResizeSensor"

import { getOptionsList } from "../../../Loaders"
import { logToServer } from "../../../ErrorBoundary"
import { API_STATUS, Required } from "../../../shared_components/widgets/utils"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { isNumber } from "../../../shared_components/utils"
import { withSampleConfig } from "../../../config/SampleConfigProvider"
import { getFormattedMessage } from "../../shared_components_registration"
import ReactSelectionElement from "../../../shared_components/widgets/ReactSelectionElement"

const generateUiId = (prefix) => {
  const random_no = Math.floor(Math.random() * 1000 + 1)
  return prefix + "_" + random_no + "_" + new Date().getTime()
}

export const useSensePageWidth = () => {
  let element = document.getElementById("application-page")
  const [pageWidth, setPageWidth] = useState(1600)

  useEffect(() => {
    // Sense the width of the page and will eliminate certain columns on page resize
    new ResizeSensor(element, () => element && setPageWidth(element.clientWidth))
    // Set initial page width
    element && setPageWidth(element.clientWidth)
  }, [element])

  useEffect(() => {
    // On unmount cleanup to avoid attempted state update on unmounted component
    return () => (element = null)
  }, [])

  return pageWidth
}

/* Form for add or update Sampling Point */
const FormSamplePoint = ({
  intl,
  // Sample Config Provider
  sample_point_error,
  sample_point_update_status,
  sample_point_add_status,
  sample_source_types = [],
  updateMetrics,
  updateSamplingPoint,
  addSamplingPoint,
  isNSView,
  // --- Sample Config Provider
  sampling_point,
  init_status,
  onHide,
}) => {
  const pageWidth = useSensePageWidth()

  const [statusInit, setStatusInit] = useState(null)
  const [statusSubmit, setStatusSubmit] = useState(null)
  const [metaData, setMetaData] = useState(null)
  const [sourceChoices, setSourceChoices] = useState([])
  const [sourceTagChoices, setSourceTagChoices] = useState([])
  const [data, setData] = useState({})
  const [errors, setErrors] = useState({})
  const [rangeErrorRow, setRangeErrorRow] = useState(null)
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false)

  useEffect(() => {
    onInitialLoad()
  }, [])

  const updateData = (name, value) => {
    setData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const onInitialLoad = () => {
    setStatusInit(API_STATUS.LOADING)

    getOptionsList("/laboratory/sample-management/sampling-point/")
      .then((data) => {
        const { frequency, type, sample_state, reactors } = data.actions.POST || {}
        const freqChoices = frequency?.choices || []
        const typeChoices = type?.choices || []
        const stateChoices = sample_state?.choices || []
        const reactorChoices = reactors?.choices || []
        const defaultType = typeChoices.filter((c) =>
          isNSView ? c.display_name !== "ANALYTICAL_SAMPLING" : c.display_name === "ANALYTICAL_SAMPLING",
        )
        if (!sampling_point) {
          defaultType.length > 0 && updateData("type", defaultType[0]?.value)
          if (isNSView) {
            updateData("sample_state", "Liquid")
            const mediaType = sample_source_types.find((t) => t.display_name === "Media")
            mediaType && updateData("default_sample_source_type", mediaType?.value)
          }
        }
        setMetaData({
          frequencies: freqChoices,
          types: defaultType,
          sampleStates: stateChoices,
          reactors: reactorChoices,
        })
        return getOptionsList(
          `/laboratory/sample-management/test-item/?view=${isNSView ? "NSRecipe" : "AnalyticalMethod"}`,
        )
      })
      .then((ti_data) => {
        const resp_data = ti_data.actions.POST
        setSourceChoices(resp_data.item_name.source_choices)
        setSourceTagChoices(resp_data.source_tag.children.source.choices)
        setStatusInit(API_STATUS.SUCCESS)
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        setStatusInit(API_STATUS.ERROR)
      })
  }

  useEffect(() => {
    setErrors(sample_point_error)
  }, [sample_point_error])

  useEffect(() => {
    if (sample_point_update_status === API_STATUS.ERROR) {
      alert(
        getFormattedMessage(
          "samplemaangement.error.updating.sample.point",
          "Error updating sample point!",
          true,
          intl,
        ),
      )
      setHasBeenSubmitted(false)
    } else if (sample_point_update_status === API_STATUS.SUCCESS) {
      onHide()
    }
    setStatusSubmit(sample_point_update_status)
  }, [sample_point_update_status])

  useEffect(() => {
    if (sample_point_add_status === API_STATUS.ERROR) {
      alert(
        getFormattedMessage(
          "samplemaangement.error.creating.sample.point",
          "Error creating sample point!",
          true,
          intl,
        ),
      )
      setHasBeenSubmitted(false)
    } else if (sample_point_add_status === API_STATUS.SUCCESS) {
      onHide()
    }
    setStatusSubmit(sample_point_add_status)
  }, [sample_point_add_status])

  useEffect(() => {
    if (!sampling_point) {
      updateData("testitem_set", [])
    } else {
      const newData = Object.fromEntries(
        Object.entries(sampling_point).map(([key, value]) => [key, value == null ? "" : value]),
      )
      newData["testitem_set"] = (newData?.testitem_set || [])
        .map((itm) => ({
          ...itm,
          _uiid: generateUiId("testitem_" + itm.id),
          item_name: itm["source_tag"].tag.name,
          method_source_id: itm["source_tag"].source.id,
          source_tag_id: itm["source_tag"].id, // Required on SP update
          unit: itm["source_tag"].units,
        }))
        .sort((a, b) => a.method_source_id - b.method_source_id)
      setData(newData)
    }
  }, [sampling_point])

  const numericalFields = [
    {
      id: "samplemanagement.min.process.range",
      label: "Min Process Range",
      field: "min_process_range",
      req: false,
      type: "number",
    },
    {
      id: "samplemanagement.max.process.range",
      label: "Max Process Range",
      field: "max_process_range",
      req: false,
      type: "number",
    },
    {
      id: "samplemanagement.min.test.range",
      label: "Min Test Range",
      field: "min_test_range",
      req: false,
      type: "number",
    },
    {
      id: "samplemanagement.max.test.range",
      label: "Max Test Range",
      field: "max_test_range",
      req: false,
      type: "number",
    },
    { id: "general.target.value", label: "Target Value", field: "target_value", req: false, type: "number" },
  ]
  const testItemHeaders = [
    { id: "general.method", label: "Method", req: true },
    { id: "general.tag", label: "Tag", req: true },
    { id: "samplemanagement.test.item", label: "Test Item", req: false },
    { id: "general.units", label: "units", req: false },
    ...numericalFields,
  ]

  const onChangeValue = (event) => {
    event.preventDefault()
    updateData(event.target.name, event.target.value)
  }

  const onChangeTestItemValue = (event, name, value, idx) => {
    event && event.preventDefault()

    let new_data = Object.assign({}, data)

    // Cast numerical strings to floats - Avoid =<> comparison btw string and number
    new_data.testitem_set[idx][name] = value && isNumber(value) ? parseFloat(value) : value

    if (name === "method_source_id") {
      new_data.testitem_set[idx] = {
        item_name: undefined,
        method_source_id: new_data.testitem_set[idx][name],
        _uiid: new_data.testitem_set[idx]["_uiid"],
      }
    } else if (numericalFields.map((f) => f["field"]).includes(name) && !(value && isNumber(value))) {
      // Remove empty string expected numerical values to avoid downstream issues trying to save an empty string
      delete new_data.testitem_set[idx][name]
    }

    setData(new_data)
  }

  const addNewTestItem = () => {
    let new_data = Object.assign({}, data)
    let testitem_set = Object.assign([], new_data.testitem_set || [])
    testitem_set.push({ _uiid: generateUiId("testitem") })

    setData({
      ...new_data,
      testitem_set: testitem_set,
    })
  }

  const deleteTestItem = (event, idx) => {
    let new_data = Object.assign({}, data)
    new_data.testitem_set.splice(idx, 1)
    setData(new_data)
  }

  const onSubmit_form = (event) => {
    event.preventDefault()
    setHasBeenSubmitted(true)

    updateMetrics(init_status)
    setRangeErrorRow(null)

    const spTestItems = data.testitem_set || []

    // ----- Validation ------
    if (spTestItems.length === 0) {
      alert(
        getFormattedMessage(
          "samplemanagement.add.tag.notification",
          "Please add at least one tag to sampling point",
          true,
          intl,
        ),
      )
      setHasBeenSubmitted(false)
      return false
    }

    let is_valid = true
    spTestItems.some((itm, i) => {
      if (!itm?.method_source_id || !itm?.item_name) {
        is_valid = false
      }

      const { min_test_range, max_test_range, min_process_range, max_process_range } = data.testitem_set[i]
      if (isNumber(min_test_range) && isNumber(max_test_range) && max_test_range <= min_test_range) {
        alert(
          getFormattedMessage(
            "samplemanagement.test.range.value.correction.message",
            "`Max Test Range` should be greater than `Min Test Range`",
            true,
            intl,
          ),
        )
        is_valid = false
      }

      if (
        is_valid &&
        isNumber(min_process_range) &&
        isNumber(max_process_range) &&
        max_process_range <= min_process_range
      ) {
        alert(
          intl.formatMessage({
            id: "samplemanagement.process.range.value.correction.message",
          }),
        )
        is_valid = false
      }

      if (
        is_valid &&
        isNumber(min_test_range) &&
        isNumber(max_test_range) &&
        isNumber(min_process_range) &&
        isNumber(max_process_range)
      ) {
        if (!(min_test_range <= min_process_range && max_process_range <= max_test_range)) {
          alert(
            intl.formatMessage({
              id: "samplemanagement.process.range.in.test.range.message",
            }),
          )
          is_valid = false
        }
      }

      if (!is_valid) {
        setRangeErrorRow(i)
        setStatusSubmit(API_STATUS.ERROR)
        return !is_valid
      }
    })

    if (!is_valid) {
      setHasBeenSubmitted(false)
      return false
    }
    // ----- /Validation ------

    // ----- Update or Add Sampling Point ------
    if (sampling_point?.id) {
      updateSamplingPoint(sampling_point.id, data)
    } else {
      addSamplingPoint(data)
    }
  }

  return (
    <div style={{ width: `${Math.max(pageWidth - 100, 850)}px` }}>
      {statusInit === API_STATUS.LOADING && <PageLoadingPanel />}

      {statusInit === API_STATUS.ERROR && <PageErrorPanel attemptReload={onInitialLoad} />}

      {statusInit === API_STATUS.SUCCESS && (
        <form name={"form_samplepoint"} className={"form-horizontal"} onSubmit={onSubmit_form}>
          {/* ----- Basic information ----- */}
          {[
            {
              field: "type",
              id: "general.type",
              label: "Type",
              disabledOnEdit: data.id,
              type: "select",
              options: metaData?.types || [],
              currentValue: { value: data["type"] },
              onValueChange: (name, opt) => updateData("type", opt?.value),
              extraProps: { isClearable: false },
            },
            {
              field: "sample_conn_no",
              id: "samplemanagement.sample.connection.number",
              label: "Sample Connection Number",
              disabledOnEdit: data.id,
              type: "text",
            },
            {
              field: "stream_name",
              id: "samplemanagement.stream.name",
              label: "Stream Name",
              disabledOnEdit: false,
              type: "text",
            },
            {
              field: "pfd_stream_no",
              id: "samplemanagement.pfd.stream.number",
              label: "PFD Stream Number",
              disabledOnEdit: false,
              type: "text",
            },
            {
              field: "sample_state",
              id: "general.state",
              label: "State",
              disabledOnEdit: data.id,
              type: "select",
              options: metaData?.sampleStates || [],
              currentValue: { value: data["sample_state"] },
              onValueChange: (name, opt) => updateData("sample_state", opt?.value),
            },
            {
              field: "reactors",
              id: "general.reactors",
              label: "Reactors",
              disabledOnEdit: data.id,
              type: "multi-select",
              options: metaData?.reactors || [],
              currentValue: (metaData?.reactors || []).filter((r) => (data.reactors || []).includes(r.value)),
              onValueChange: (name, opts) =>
                updateData(
                  "reactors",
                  opts.map((opt) => opt.value),
                ),
              extraProps: { getOptionLabel: (obj) => obj.display_name },
            },
            {
              field: "prefix_for_sampleid",
              id: "samplemanagement.sample.prefix",
              label: "Sample Prefix",
              disabledOnEdit: false,
              type: "text",
              extraProps: {
                placeholder: `${intl.formatMessage({
                  id: "samplemanagement.sample.prefix.placeholder",
                })} (${intl.formatMessage({
                  id: "general.must.be.unique",
                })})`,
              },
            },
            {
              field: "frequency",
              id: "samplemanagement.frequency",
              label: "Frequency",
              disabledOnEdit: false,
              type: "select",
              options: metaData?.frequencies || [],
              currentValue: { value: data["frequency"] },
              onValueChange: (name, opt) => updateData("frequency", opt?.value),
            },
            {
              field: "default_sample_source_type",
              id: "samplemanagement.default.sample.source.type",
              label: "Default Sample Source Type",
              disabledOnEdit: false,
              type: "select",
              options: sample_source_types,
              currentValue: sample_source_types.find((t) => t.value === data["default_sample_source_type"]),
              onValueChange: (name, opt) => updateData("default_sample_source_type", opt?.value),
              extraProps: { getOptionLabel: (obj) => obj.display_name },
            },
          ].map((field, fIDX) => (
            <div
              key={`field${fIDX}`}
              className={`row one-rem-margin-bottom ${errors[field["field"]] ? "has-error" : ""}`}
              style={{ marginBottom: "10px", fontSize: "90%" }}
            >
              <span className={"col-sm-1"} />
              <label className={"col-sm-3 col-form-label"}>
                {getFormattedMessage(field["id"], field["label"])}
                <Required />:
              </label>
              &nbsp;
              <div className={"col-sm-7"}>
                {field["type"] === "text" && (
                  <input
                    type={"text"}
                    className={"form-control"}
                    name={field["field"]}
                    placeholder={getFormattedMessage(field["id"], field["label"], true, intl)}
                    value={data[field["field"]] || ""}
                    onChange={onChangeValue}
                    disabled={field["disabledOnEdit"]}
                    {...(field["extraProps"] || {})}
                  />
                )}
                {["select", "multi-select"].includes(field["type"]) && (
                  <ReactSelectionElement
                    name={field["field"]}
                    list={field["options"]}
                    value={data[field["field"]] ? field["currentValue"] : null}
                    disabled={field["disabledOnEdit"]}
                    onValueChange={field["onValueChange"]}
                    isMulti={field["type"] === "multi-select"}
                    {...(field["extraProps"] || {})}
                  />
                )}
                {errors[field["field"]] && (
                  <span className={"form-text text-danger"}>{errors[field["field"]]}</span>
                )}
              </div>
            </div>
          ))}
          {/* ----- /Basic information ----- */}

          {/* ----- Test Items ----- */}
          <div>
            <hr style={{ margin: "10px 0px" }} />
            <table className={"table table-condensed tbl_lanzatable"}>
              <thead>
                <tr>
                  {testItemHeaders.map((header) => (
                    <th className={"text-center"} key={header["id"]} style={{ fontSize: "90%" }}>
                      {getFormattedMessage(header["id"], header["label"])}
                      {header["req"] && <Required />}
                    </th>
                  ))}
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {data.testitem_set.map((test_item, idx) => {
                  // SourceTag(s) for Source already in use for this Sampling Point
                  let selected_options = data.testitem_set
                    .filter((ti) => test_item.method_source_id === ti.method_source_id && ti.item_name)
                    .map((ti) => ti.item_name)
                  // Available tag option(s) for chosen Source/Method
                  const srcTags = sourceChoices?.[test_item?.method_source_id] || []
                  const tag_options = srcTags.filter(
                    (st) => !selected_options.includes(st.value) || test_item.item_name === st.value,
                  )
                  const getSourceTag = (val) => srcTags.find((st) => st.value === val)
                  return (
                    <tr
                      key={test_item._uiid}
                      style={{
                        backgroundColor:
                          idx === rangeErrorRow ? "var(--lanzalake-red-light)" : "var(--lanzalake-white)",
                      }}
                    >
                      {[
                        {
                          field: "method_source_id",
                          disabled: test_item.id,
                          type: test_item.id ? "string" : "select",
                          options: sourceTagChoices || [],
                          currentValue: test_item.id
                            ? (sourceTagChoices || []).find(
                                (st) => st.value === test_item["method_source_id"],
                              )?.display_name
                            : (sourceTagChoices || []).find(
                                (st) => st.value === test_item["method_source_id"],
                              ),
                          onValueChange: (name, opt) => onChangeTestItemValue(null, name, opt?.value, idx),
                          extraProps: { getOptionLabel: (obj) => obj.display_name },
                        },
                        {
                          field: "item_name",
                          disabled: test_item.id || !test_item.method_source_id || tag_options.length === 0,
                          type: test_item.id ? "string" : "select",
                          options: tag_options,
                          currentValue: test_item.id
                            ? getSourceTag(test_item["item_name"])?.value
                            : getSourceTag(test_item["item_name"]),
                          onValueChange: (name, opt) => {
                            const tagName = opt?.value
                            onChangeTestItemValue(null, name, tagName, idx)
                            onChangeTestItemValue(null, "unit", getSourceTag(tagName)?.unit, idx)
                          },
                        },
                        {
                          field: "label",
                          type: "text",
                        },
                        {
                          field: "unit",
                          type: "string",
                          currentValue: test_item?.unit,
                        },
                        ...numericalFields,
                      ].map((field, fIDX) => (
                        <td
                          key={`row${idx}field${fIDX}`}
                          className={"text-center"}
                          style={{
                            ...(["number", "text"].includes(field["type"])
                              ? { width: field["type"] === "number" ? "85px" : "160px" }
                              : {}),
                            fontSize: "90%",
                          }}
                        >
                          {field["type"] === "string" && <span>{field["currentValue"] || "-"}</span>}
                          {field["type"] === "select" && (
                            <ReactSelectionElement
                              name={field["field"]}
                              list={field["options"]}
                              value={test_item[field["field"]] ? field["currentValue"] : null}
                              disabled={field["disabled"]}
                              onValueChange={field["onValueChange"]}
                              isMulti={false}
                              isClearable={false}
                              {...(field["extraProps"] || {})}
                            />
                          )}
                          {["text", "number"].includes(field["type"]) && (
                            <input
                              type={field["type"]}
                              className={"form-control form-control-sm"}
                              name={field.field}
                              onChange={(e) => onChangeTestItemValue(e, e.target.name, e.target.value, idx)}
                              value={
                                field["type"] === "number"
                                  ? isNumber(test_item[field["field"]])
                                    ? test_item[field["field"]]
                                    : ""
                                  : test_item[field.field] || ""
                              }
                              style={{ fontSize: "90%" }}
                              {...(field["type"] === "number" ? { onWheel: (e) => e.target.blur() } : {})}
                            />
                          )}
                        </td>
                      ))}
                      <td>
                        <Button
                          variant={"outline-primary"}
                          disabled={hasBeenSubmitted}
                          size={"sm"}
                          onClick={(event) => deleteTestItem(event, idx)}
                        >
                          <i className={"fa fa-times text-danger"} />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <Button variant={"outline-primary"} size={"sm"} onClick={addNewTestItem}>
              <i className={"fa fa-plus text-primary"} />
              &nbsp;&nbsp;
              {getFormattedMessage("samplemanagement.add.test.item", "Add Test Item")}
            </Button>
          </div>
          {/* ----- /Test Items ----- */}

          {errors && (
            <div style={{ marginTop: "20px" }}>
              {Array.isArray(errors) ? (
                <>
                  {errors.map((err, i) => {
                    return (
                      <p key={i} className={"text-danger"}>
                        {err}
                      </p>
                    )
                  })}
                </>
              ) : typeof errors == "string" ? (
                <p className={"text-danger"}>{errors}</p>
              ) : (
                <></>
              )}
            </div>
          )}

          <hr style={{ margin: "10px 0px" }} />
          <div className={"row"}>
            <div className={"col-7"}>
              {statusSubmit === API_STATUS.ERROR && (
                <span className={"pull-left text-danger"}>
                  {getFormattedMessage("general.please.correct.errors", "Please Correct Errors")}
                </span>
              )}
            </div>
            <div className={"col-5"} align={"right"}>
              <Button variant={"outline-primary"} onClick={onHide}>
                {getFormattedMessage("general.cancelButton", "Cancel")}
              </Button>
              &nbsp;&nbsp;
              <Button variant={"primary"} type={"submit"}>
                {getFormattedMessage("general.submitButton", "Submit")}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}

export default withSampleConfig(FormSamplePoint)

FormSamplePoint.propTypes = {
  intl: PropTypes.object.isRequired,
  sample_point_error: PropTypes.object,
  sample_point_update_status: PropTypes.string,
  sample_point_add_status: PropTypes.string,
  sample_source_types: PropTypes.array,
  updateMetrics: PropTypes.func.isRequired,
  updateSamplingPoint: PropTypes.func.isRequired,
  addSamplingPoint: PropTypes.func.isRequired,
  sampling_point: PropTypes.object,
  init_status: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
}
